import { memo, useCallback, useEffect, useState } from 'react'
import {
  Box,
  Typography,
  TextField,
  Chip,
  Button,
  Stack,
  Divider,
  Alert,
  CircularProgress,
} from '@mui/material'
import SaveIcon from '@mui/icons-material/Save'
import RefreshIcon from '@mui/icons-material/Refresh'
import { OrcaQueryFieldEditor } from './OrcaQueryFieldEditor.js'
import { ConfirmDeleteButton } from './ConfirmDeleteButton.js'
import type {
  OrcaQueryDefinition,
  OrcaQueryFieldAnnotation,
  OrcaQueryTagAnnotation,
} from './types.js'

type Props = {
  initial: OrcaQueryDefinition
  /** Persist the draft; when requestRefresh is set, also (re-)resolve the tags. */
  onSave: (draft: OrcaQueryDefinition, requestRefresh: boolean) => Promise<void>
  /** Archive (delete) this query. */
  onArchive: () => Promise<void>
}

/**
 * Displays exactly the server's field set (so fields the server dropped
 * disappear), overlaying the local draft's human text for surviving fields and
 * refreshing their generated type.
 */
const mergeServerFields = (
  local: Record<string, OrcaQueryFieldAnnotation>,
  server: Record<string, OrcaQueryFieldAnnotation>,
): Record<string, OrcaQueryFieldAnnotation> => {
  const merged: Record<string, OrcaQueryFieldAnnotation> = {}
  for (const [name, sf] of Object.entries(server)) {
    const lf = local[name]
    merged[name] = lf ? { ...lf, type: sf.type } : sf
  }
  return merged
}

/** Displays exactly the server's observed-tag set, keeping local descriptions. */
const mergeServerTags = (
  local: Record<string, OrcaQueryTagAnnotation>,
  server: Record<string, OrcaQueryTagAnnotation>,
): Record<string, OrcaQueryTagAnnotation> => {
  const merged: Record<string, OrcaQueryTagAnnotation> = {}
  for (const [tag, st] of Object.entries(server)) {
    merged[tag] = local[tag] ?? st
  }
  return merged
}

type ObservedTagRowProps = {
  tag: string
  description: string
  onChange: (tag: string, description: string) => void
}

/**
 * One annotatable observed-tag row. Memoized so typing in one row does not
 * re-render every other row (queries can have many observed tags).
 */
const ObservedTagRow = memo(
  ({ tag, description, onChange }: ObservedTagRowProps) => (
    <Box display="flex" gap={1.5} alignItems="center">
      <Chip
        label={tag}
        size="small"
        sx={{
          fontFamily: 'monospace',
          fontSize: 11,
          minWidth: 120,
          justifyContent: 'flex-start',
        }}
      />
      <TextField
        size="small"
        fullWidth
        value={description}
        onChange={(e) => onChange(tag, e.target.value)}
        placeholder="Was bedeutet dieser Tag?"
      />
    </Box>
  ),
)
ObservedTagRow.displayName = 'ObservedTagRow'

/**
 * Form to define and document one saved Orca query: name, description, the AND
 * tag set, and — after resolving against the live Orca schema — the exposed
 * fields and observed tags. Field types are generated (read-only); the user
 * supplies meanings, units, examples and tag descriptions.
 */
export const OrcaQueryForm = ({ initial, onSave, onArchive }: Props) => {
  const [name, setName] = useState(initial.name)
  const [description, setDescription] = useState(initial.description ?? '')
  const [tags, setTags] = useState<string[]>(initial.tags ?? [])
  const [tagInput, setTagInput] = useState('')
  const [fields, setFields] = useState<
    Record<string, OrcaQueryFieldAnnotation>
  >(initial.fields ?? {})
  const [observedTags, setObservedTags] = useState<
    Record<string, OrcaQueryTagAnnotation>
  >(initial.observedTags ?? {})
  const [busy, setBusy] = useState(false)
  const [saved, setSaved] = useState(false)

  // The reconciler fills fields/observedTags live after a refresh; merge those
  // in without discarding annotations the user is editing.
  useEffect(() => {
    setFields((local) => mergeServerFields(local, initial.fields ?? {}))
  }, [initial.fields])
  useEffect(() => {
    setObservedTags((local) =>
      mergeServerTags(local, initial.observedTags ?? {}),
    )
  }, [initial.observedTags])

  const persist = async (requestRefresh: boolean) => {
    setBusy(true)
    await onSave(
      { name, description, tags, fields, observedTags },
      requestRefresh,
    )
    setBusy(false)
    if (!requestRefresh) {
      setSaved(true)
      setTimeout(() => setSaved(false), 2500)
    }
  }

  const addTag = () => {
    const tag = tagInput.trim()
    if (tag && !tags.includes(tag)) setTags([...tags, tag])
    setTagInput('')
  }

  // Stable callbacks + functional updates so memoized rows keep referential
  // equality: only the edited field/tag re-renders, not the whole list.
  const updateField = useCallback(
    (fieldName: string, updated: OrcaQueryFieldAnnotation) =>
      setFields((prev) => ({ ...prev, [fieldName]: updated })),
    [],
  )
  const updateTag = useCallback(
    (tag: string, tagDescription: string) =>
      setObservedTags((prev) => ({
        ...prev,
        [tag]: { description: tagDescription },
      })),
    [],
  )

  const fieldNames = Object.keys(fields)
  const tagNames = Object.keys(observedTags)
  const pending =
    initial.refreshRequestedAt !== undefined &&
    (initial.resolvedAt === undefined ||
      initial.refreshRequestedAt > initial.resolvedAt)

  return (
    <Box p={4} maxWidth={760}>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
        gap={2}
      >
        <TextField
          label="Name der Abfrage"
          size="small"
          value={name}
          onChange={(e) => setName(e.target.value)}
          sx={{ flex: 1 }}
        />
        <Box display="flex" gap={1}>
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={() => persist(false)}
            disabled={busy}
            color={saved ? 'success' : 'primary'}
          >
            {busy ? 'Speichern…' : saved ? 'Gespeichert' : 'Speichern'}
          </Button>
          <ConfirmDeleteButton
            onConfirm={onArchive}
            itemLabel={initial.name}
            disabled={busy}
          />
        </Box>
      </Box>

      {initial.resolveError && (
        <Alert severity="error" sx={{ mb: 3 }}>
          Auflösung fehlgeschlagen: {initial.resolveError}
        </Alert>
      )}
      {saved && (
        <Alert severity="success" sx={{ mb: 3 }}>
          Abfrage gespeichert.
        </Alert>
      )}

      <Stack spacing={3}>
        <TextField
          label="Beschreibung"
          multiline
          rows={3}
          fullWidth
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Was liefert diese Abfrage? In welchem Kontext entstehen die Daten?"
        />

        <Box>
          <Typography variant="subtitle2" fontWeight={600} mb={1}>
            Tags (UND-verknüpft)
          </Typography>
          <Box display="flex" flexWrap="wrap" gap={1} mb={1.5}>
            {tags.map((tag) => (
              <Chip
                key={tag}
                label={tag}
                size="small"
                onDelete={() => setTags(tags.filter((t) => t !== tag))}
                sx={{ fontFamily: 'monospace', fontSize: 11 }}
              />
            ))}
            {tags.length === 0 && (
              <Typography variant="body2" color="text.disabled">
                Noch keine Tags. Alle angegebenen Tags werden UND-verknüpft.
              </Typography>
            )}
          </Box>
          <Box display="flex" gap={1} alignItems="center">
            <TextField
              size="small"
              value={tagInput}
              onChange={(e) => setTagInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && addTag()}
              placeholder="Tag hinzufügen…"
            />
            <Button variant="outlined" size="small" onClick={addTag}>
              +
            </Button>
            <Box flex={1} />
            <Button
              variant="outlined"
              startIcon={
                pending ? <CircularProgress size={16} /> : <RefreshIcon />
              }
              onClick={() => persist(true)}
              disabled={busy || pending || tags.length === 0}
            >
              {pending ? 'Wird aufgelöst…' : 'Schema laden/aktualisieren'}
            </Button>
          </Box>
        </Box>

        <Box>
          <Typography variant="subtitle2" fontWeight={600} mb={1.5}>
            Felder (aus dem gemergten Orca-Schema)
          </Typography>
          <Divider sx={{ mb: 2 }} />
          {fieldNames.length === 0 ? (
            <Typography variant="body2" color="text.disabled">
              Noch keine Felder. Tags eingeben und „Schema laden/aktualisieren"
              klicken.
            </Typography>
          ) : (
            <Stack spacing={1}>
              {fieldNames.map((fname) => (
                <OrcaQueryFieldEditor
                  key={fname}
                  name={fname}
                  annotation={fields[fname]}
                  onChange={updateField}
                />
              ))}
            </Stack>
          )}
        </Box>

        <Box>
          <Typography variant="subtitle2" fontWeight={600} mb={1.5}>
            Beobachtete Tags
          </Typography>
          <Divider sx={{ mb: 2 }} />
          {tagNames.length === 0 ? (
            <Typography variant="body2" color="text.disabled">
              Noch keine beobachteten Tags.
            </Typography>
          ) : (
            <Stack spacing={1.5}>
              {tagNames.map((tag) => (
                <ObservedTagRow
                  key={tag}
                  tag={tag}
                  description={observedTags[tag].description}
                  onChange={updateTag}
                />
              ))}
            </Stack>
          )}
        </Box>
      </Stack>
    </Box>
  )
}
